/* ================================================================
   ACE STRATEGIES — Guides
   Behaviour for the /guides/ long-form reference pages.

   Loaded per-page via `extra_js`, which base.njk emits with `defer`.

   Everything here is an upgrade to markup that already works. The TOC is
   real anchors, the checklist is real checkboxes, the FAQ is <details>,
   and the download link is a plain href. If this file fails to load, or
   localStorage throws, or the analytics tag is absent, the page still
   does its job. Nothing below is allowed to be load-bearing.

   No inline handlers anywhere: scripts/verify-inline-handlers.js fails the
   build on one, and the CSP in worker.js depends on that staying true.
   ================================================================ */
(function () {
  "use strict";

  var guide = document.querySelector(".guide");
  if (!guide) return;

  var slug = document.body.getAttribute("data-guide") || "guide";

  /* ── Analytics ────────────────────────────────────────────────
     One entry point. The site runs gtag; if the tag has not loaded —
     consent declined, blocker, offline — this silently does nothing
     rather than throwing inside an event handler and taking the rest
     of the interaction with it. */
  function track(action, params) {
    try {
      var payload = Object.assign({ guide: slug }, params || {});
      if (typeof window.gtag === "function") {
        window.gtag("event", action, payload);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: action }, payload));
      }
    } catch (e) {
      /* analytics must never break the page */
    }
  }

  /* ── Storage ──────────────────────────────────────────────────
     Private browsing, disabled storage and quota errors all throw on
     access, not just on write, so every touch goes through here. */
  var store = (function () {
    try {
      var probe = "__g" + Date.now();
      window.localStorage.setItem(probe, "1");
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  })();

  var storeKey = "aces-guide:" + slug;

  function readState() {
    if (!store) return {};
    try {
      return JSON.parse(store.getItem(storeKey) || "{}") || {};
    } catch (e) {
      return {};
    }
  }

  function writeState(state) {
    if (!store) return;
    try {
      store.setItem(storeKey, JSON.stringify(state));
    } catch (e) {
      /* quota or a mode that allows reads and refuses writes */
    }
  }

  /* ── Heading anchors ──────────────────────────────────────────
     Added here rather than in the template so the markup stays clean
     and a heading cannot end up with an anchor pointing at no id. */
  var body = guide.querySelector(".guide__body");
  if (body) {
    body.querySelectorAll("h2[id], h3[id]").forEach(function (h) {
      var a = document.createElement("a");
      a.className = "g-anchor";
      a.href = "#" + h.id;
      a.setAttribute("aria-label", "Link to this section — " + h.textContent.trim());
      a.textContent = "#";
      h.appendChild(a);
    });
  }

  /* ── Table of contents ────────────────────────────────────────
     Scrollspy by IntersectionObserver rather than a scroll handler.
     rootMargin pins the "current" band to the top third of the
     viewport, so a heading counts as current once it reaches reading
     position, not when it first appears at the bottom. */
  var toc = document.querySelector(".g-toc");
  if (toc && body) {
    var links = Array.prototype.slice.call(toc.querySelectorAll(".g-toc__link"));
    var byId = {};
    links.forEach(function (l) {
      byId[l.getAttribute("href").slice(1)] = l;
      l.addEventListener("click", function () {
        track("guide_toc_click", { section: l.textContent.trim() });
      });
    });

    var targets = links
      .map(function (l) { return document.getElementById(l.getAttribute("href").slice(1)); })
      .filter(Boolean);

    if ("IntersectionObserver" in window && targets.length) {
      var visible = new Set();
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) visible.add(entry.target.id);
            else visible.delete(entry.target.id);
          });
          // The topmost visible heading wins, so scrolling up highlights
          // the section being entered rather than the one being left.
          var current = targets.filter(function (t) { return visible.has(t.id); })[0];
          if (!current) return;
          links.forEach(function (l) { l.removeAttribute("aria-current"); });
          if (byId[current.id]) byId[current.id].setAttribute("aria-current", "true");
        },
        { rootMargin: "-96px 0px -66% 0px", threshold: 0 }
      );
      targets.forEach(function (t) { observer.observe(t); });
    }

    // Mobile: the open/closed bar collapses to a progress line once the
    // reader is into the article.
    var progress = toc.querySelector(".g-toc__progress");
    if (progress) {
      var ticking = false;
      window.addEventListener(
        "scroll",
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            var rect = body.getBoundingClientRect();
            var total = rect.height - window.innerHeight;
            var done = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
            progress.style.width = (done * 100).toFixed(1) + "%";
            ticking = false;
          });
        },
        { passive: true }
      );
    }
  }

  /* ── Checklists ───────────────────────────────────────────────
     Restore, then persist. Restoration is skipped entirely when storage
     is unavailable — the boxes are ordinary checkboxes either way. */
  var state = readState();
  var boxes = Array.prototype.slice.call(guide.querySelectorAll(".g-check__box"));
  boxes.forEach(function (box) {
    var key = box.getAttribute("data-check-key");
    if (state[key]) box.checked = true;
    box.addEventListener("change", function () {
      var next = readState();
      if (box.checked) next[key] = 1;
      else delete next[key];
      writeState(next);
      track("guide_checklist_toggle", { item: key, checked: box.checked });
    });
  });

  // "Print all checklists" — the print stylesheet does the selection.
  if (boxes.length) {
    var last = guide.querySelector(".g-check:last-of-type");
    if (last) {
      var printBtn = document.createElement("button");
      printBtn.type = "button";
      printBtn.className = "g-check__print";
      printBtn.innerHTML =
        '<span class="material-symbols-outlined" aria-hidden="true">print</span>Print all checklists';
      printBtn.addEventListener("click", function () {
        track("guide_print_checklists", {});
        window.print();
      });
      last.appendChild(printBtn);
    }
  }

  /* ── FAQ ──────────────────────────────────────────────────────
     <details> already handles open/closed, keyboard operation and
     find-in-page. Added here: arrow-key movement between questions,
     which <details> does not provide, and the open event. */
  var faq = document.querySelector("[data-guide-faq]");
  if (faq) {
    var summaries = Array.prototype.slice.call(faq.querySelectorAll(".g-faq__q"));
    summaries.forEach(function (s, i) {
      s.addEventListener("keydown", function (e) {
        var next = null;
        if (e.key === "ArrowDown") next = summaries[i + 1];
        else if (e.key === "ArrowUp") next = summaries[i - 1];
        else if (e.key === "Home") next = summaries[0];
        else if (e.key === "End") next = summaries[summaries.length - 1];
        if (!next) return;
        e.preventDefault();
        next.focus();
      });
      s.parentElement.addEventListener("toggle", function () {
        if (s.parentElement.open) {
          track("guide_faq_open", { question: s.textContent.trim() });
        }
      });
    });
  }

  /* ── Cheat sheet: copy as text ────────────────────────────────
     The button is created here, not in the template, because without
     this script it would do nothing. */
  var cheat = document.querySelector("[data-guide-cheatsheet]");
  if (cheat && navigator.clipboard) {
    var copy = document.createElement("button");
    copy.type = "button";
    copy.className = "g-cheat__copy";
    copy.innerHTML =
      '<span class="material-symbols-outlined" aria-hidden="true">content_copy</span>Copy as text';
    copy.addEventListener("click", function () {
      var lines = [];
      cheat.querySelectorAll("tbody tr").forEach(function (tr) {
        var cells = Array.prototype.map.call(tr.children, function (c) {
          return c.textContent.trim();
        });
        lines.push(cells.join(" — "));
      });
      var text =
        "The strategy on one page\n\n" + lines.join("\n") + "\n\nacestrategies.au" +
        window.location.pathname;
      navigator.clipboard.writeText(text).then(
        function () {
          copy.lastChild.nodeValue = "Copied";
          track("guide_cheatsheet_copy", {});
          window.setTimeout(function () { copy.lastChild.nodeValue = "Copy as text"; }, 2000);
        },
        function () {
          copy.lastChild.nodeValue = "Press Ctrl+C to copy";
        }
      );
    });
    cheat.appendChild(copy);
  }

  /* ── Template form ────────────────────────────────────────────
     Posts to /newsletter/subscribe, the double opt-in endpoint that
     already exists. Validation is inline and the download link beside
     it is never gated behind script — someone whose JS failed still
     gets the thing the page promised them. */
  document.querySelectorAll("[data-guide-form]").forEach(function (form) {
    var input = form.querySelector(".g-tpl__input");
    var error = form.querySelector(".g-tpl__error");
    var variant = form.getAttribute("data-guide-form");

    function fail(message) {
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      input.setAttribute("aria-invalid", "true");
      input.focus();
    }

    function clear() {
      if (!error) return;
      error.hidden = true;
      input.removeAttribute("aria-invalid");
    }

    input.addEventListener("input", clear);

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var value = (input.value || "").trim();
      // Deliberately permissive. The endpoint verifies by sending to the
      // address; a strict pattern here only rejects valid addresses.
      if (!value || value.indexOf("@") < 1 || value.lastIndexOf(".") < value.indexOf("@")) {
        fail("Enter an email address we can send the template to.");
        return;
      }
      clear();
      track("guide_template_submit", { variant: variant });

      /* Posted with fetch rather than natively. /newsletter/subscribe answers
         JSON, so a native post navigates the reader away from the guide and
         onto a page of raw JSON — which is what it did, on every attempt,
         because the form carried no Turnstile token and the endpoint fails
         closed without one. */
      var btn = form.querySelector(".g-tpl__btn");
      var originalText = btn ? btn.textContent : "";
      if (btn) { btn.textContent = "Sending…"; btn.disabled = true; }

      var resetTurnstile = function () {
        var widget = form.querySelector(".cf-turnstile");
        if (widget && window.turnstile) window.turnstile.reset(widget);
      };
      var restore = function () {
        if (btn) { btn.textContent = originalText; btn.disabled = false; }
        resetTurnstile();
      };

      var body = new URLSearchParams();
      new FormData(form).forEach(function (v, k) { body.append(k, v); });

      fetch("/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json"
        },
        body: body.toString()
      })
        .then(function (res) {
          if (res.ok) {
            if (btn) btn.textContent = "✓ Check your email";
            form.reset();
            resetTurnstile();
            return;
          }
          return res.json().catch(function () { return {}; }).then(function (data) {
            restore();
            if (data.error === "rate_limited") {
              fail("Too many attempts — please try again in a minute.");
            } else if (data.error === "verification_failed") {
              fail("We could not verify you are human. Please try again.");
            } else if (data.error === "invalid_email") {
              fail("Please enter a valid email address.");
            } else {
              fail("Something went wrong. The download link above still works.");
            }
          });
        })
        .catch(function () {
          restore();
          fail("Something went wrong. The download link above still works.");
        });
    });
  });

  document.querySelectorAll("[data-guide-download]").forEach(function (link) {
    link.addEventListener("click", function () {
      track("guide_template_download", { variant: link.getAttribute("data-guide-download") });
    });
  });

  /* ── Scroll depth ─────────────────────────────────────────────
     Each threshold fires once. Measured against the article, not the
     document, so the footer does not count as reading. */
  if (body) {
    var marks = [25, 50, 75, 100];
    var fired = {};
    var pending = false;
    window.addEventListener(
      "scroll",
      function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          var rect = body.getBoundingClientRect();
          var total = rect.height - window.innerHeight;
          var pct = total > 0 ? Math.min(100, Math.max(0, (-rect.top / total) * 100)) : 100;
          marks.forEach(function (m) {
            if (!fired[m] && pct >= m) {
              fired[m] = true;
              track("guide_scroll_depth", { depth: m });
            }
          });
        });
      },
      { passive: true }
    );
  }
})();
